import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  UserGroupIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  FolderIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { Team } from '../../types';
import { usersAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';

interface MyTeamsProps {
  teams: Team[];
}

interface TeamInsight {
  id: string;
  name: string;
  color: string;
  description?: string;
  manager: any;
  members: any[];
  insights: {
    totalTasks: number;
    userTasks: number;
    completedTasks: number;
    completionRate: number;
    isManager: boolean;
  };
}

const MyTeams: React.FC<MyTeamsProps> = ({ teams }) => {
  const { user } = useAuth();
  const [teamInsights, setTeamInsights] = useState<TeamInsight[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadTeamInsights();
    }
  }, [user, teams]);

  const loadTeamInsights = async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      const insights = await usersAPI.getUserTeamInsights(user.id);
      setTeamInsights(insights);
    } catch (error) {
      console.error('Error loading team insights:', error);
      // Fallback to basic team data
      setTeamInsights(teams.map(team => ({
        ...team,
        insights: {
          totalTasks: 0,
          userTasks: 0,
          completedTasks: 0,
          completionRate: 0,
          isManager: team.manager.id === user.id,
        }
      })));
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">My Teams</h2>
        <div className="text-sm text-gray-500">
          {teamInsights.length} team{teamInsights.length !== 1 ? 's' : ''}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {teamInsights.map((team, index) => (
          <motion.div
            key={team.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: team.color }}
                />
                <h3 className="text-lg font-semibold text-gray-900">{team.name}</h3>
              </div>
              {team.insights.isManager && (
                <div className="flex items-center space-x-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                  <StarIcon className="w-3 h-3" />
                  <span>Manager</span>
                </div>
              )}
            </div>

            {team.description && (
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">{team.description}</p>
            )}

            {/* Team Insights */}
            <div className="space-y-4 mb-4">
              {/* Task Statistics */}
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-lg font-bold text-blue-600">{team.insights.userTasks}</div>
                  <div className="text-xs text-gray-600">My Tasks</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-lg font-bold text-green-600">{Math.round(team.insights.completionRate)}%</div>
                  <div className="text-xs text-gray-600">Complete</div>
                </div>
              </div>

              {/* Progress Bar */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Team Progress</span>
                  <span className="text-gray-900 font-medium">
                    {team.insights.completedTasks} / {team.insights.totalTasks}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${team.insights.completionRate}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                Manager
              </p>
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                  <span className="text-xs font-medium text-white">
                    {team.manager.firstName[0]}{team.manager.lastName[0]}
                  </span>
                </div>
                <span className="text-sm text-gray-900">
                  {team.manager.firstName} {team.manager.lastName}
                </span>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                Members ({team.members.length})
              </p>
              <div className="flex flex-wrap gap-1">
                {team.members.slice(0, 8).map((member) => (
                  <div
                    key={member.id}
                    className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center"
                    title={`${member.user.firstName} ${member.user.lastName}`}
                  >
                    <span className="text-xs font-medium text-gray-700">
                      {member.user.firstName[0]}{member.user.lastName[0]}
                    </span>
                  </div>
                ))}
                {team.members.length > 8 && (
                  <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-gray-600">
                      +{team.members.length - 8}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Team Stats */}
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div>
                <div className="font-medium text-gray-900">{team.insights.totalTasks}</div>
                <div className="text-gray-500">Total Tasks</div>
              </div>
              <div>
                <div className="font-medium text-gray-900">{team.members.length}</div>
                <div className="text-gray-500">Members</div>
              </div>
              <div>
                <div className="font-medium text-gray-900">{team.insights.completedTasks}</div>
                <div className="text-gray-500">Completed</div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {teamInsights.length === 0 && (
        <div className="text-center py-12">
          <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No teams</h3>
          <p className="mt-1 text-sm text-gray-500">
            You're not a member of any teams yet.
          </p>
        </div>
      )}
    </div>
  );
};

export default MyTeams;
